import useOfferList from 'hooks/Offers/useOfferList'
import React, { useCallback, useMemo, useState } from 'react'
import { MAX_CAROUSEL_ITEM_COUNT } from './common/constants'
import { LOOKING_FOR_SOMETHING_ELSE_CONTENT } from '../LookingForSomethingElse/constants'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import styled from 'styled-components'
import { rem } from 'polished'
import OfferCarouselHeader from './common/OfferCarouselHeader'
import OfferCarouselFilters from './common/OfferCarouselFilters'
import { trendingCruiseDestinations } from '../TrendingDestinations/constants'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import { SORTBY_VERTICAL_CRUISE } from 'constants/offerList'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import OfferCarouselLookingForSomethingElse from '../LookingForSomethingElse/OfferCarouselLookingForSomethingElse'

const DEFAULT_FILTERS: App.OfferListFilters = {
  offerTypes: ['cruise'],
  frontPageVisibleOnly: true,
  limit: MAX_CAROUSEL_ITEM_COUNT,
}

const UNIFIED_FILTERS: App.OfferListFilters = {
  offerTypes: ['tour', 'cruise', 'hotel'],
  holidayTypes: ['Cruises'],
  sortBy: SORTBY_VERTICAL_CRUISE,
  limit: MAX_CAROUSEL_ITEM_COUNT,
}

const SectionContainer = styled(VerticalSpacer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

const ANYWHERE_DESTINATION_FILTER = {
  name: 'Around the world',
  destinationId: ANYWHERE_PLACE_ID,
}

type CruiseFilter = {
  name: string;
  destinationId: string;
}

interface Props {
  title?: React.ReactNode;
}

function CruiseOfferCarousel(props: Props) {
  const { title } = props
  const [selectedFilter, setSelectedFilter] = useState<CruiseFilter>(ANYWHERE_DESTINATION_FILTER)
  const filterType = isManualSortingEnabledFor('cruises') ? UNIFIED_FILTERS : DEFAULT_FILTERS

  const offerListFilters = useMemo(() => ({
    ...filterType,
    destinationId: selectedFilter.destinationId,
    destinationName: selectedFilter.name,
  }), [filterType, selectedFilter.destinationId, selectedFilter.name])

  const offerList = useOfferList(offerListFilters)

  const filters = useMemo(() => {
    // for cruises, destinationId should never be undefined
    return [
      ANYWHERE_DESTINATION_FILTER,
      ...(trendingCruiseDestinations.AU ?? []).map(({ name, destinationId }) => ({ name, destinationId: destinationId ?? ANYWHERE_PLACE_ID })),
    ]
  }, [])

  const onFilterClick = useCallback((name: string) => {
    if (selectedFilter.name === name) {
      return
    }
    const newSelection = filters.find(filter => filter.name === name)
    if (newSelection) {
      setSelectedFilter(newSelection)
    }
  }, [filters, selectedFilter.name])

  const filterOptionNames = useMemo(() => filters.map(filter => filter.name), [filters])

  const to = useMemo(() => {
    if (offerListFilters.destinationId && offerListFilters.destinationId !== ANYWHERE_PLACE_ID) {
      const query = {
        portId: offerListFilters.destinationId,
        portName: offerListFilters.destinationName,
      }
      return `/search/cruises?${new URLSearchParams(query)}`
    }
    return LOOKING_FOR_SOMETHING_ELSE_CONTENT.cruises.to
  }, [offerListFilters])

  if (offerList.offerIds.length === 0 && filters.length === 0) {
    return null
  }

  return (
    <SectionContainer gap={20}>
      <LayoutContainer>
        <VerticalSpacer gap={20}>
          <OfferCarouselHeader allTo="/cruises">
            {title ?? <>Set sail on the ultimate <i>cruise</i></>}
          </OfferCarouselHeader>
          <OfferCarouselFilters
            options={filterOptionNames}
            active={selectedFilter.name}
            onClick={onFilterClick}
          />
        </VerticalSpacer>
      </LayoutContainer>
      <OfferListCarousel
        filters={offerListFilters}
        mobileLimit={8}
      >
        <OfferCarouselLookingForSomethingElse to={to} />
      </OfferListCarousel>
    </SectionContainer>
  )
}

export default React.memo(CruiseOfferCarousel)
